import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScanLine, Sparkles, AlertCircle, Hash } from "lucide-react";
import { toast } from "sonner";

const ADAS_SYSTEMS = [
  { key: "front_camera", name: "Front-facing Camera (Lane Assist / FCW)" },
  { key: "front_radar", name: "Front Radar (ACC / AEB / FCM)" },
  { key: "parking_sensors", name: "Parking Sensors / Ultrasonic" },
  { key: "blind_spot_radar", name: "Blind Spot Monitor (Rear Radar)" },
  { key: "bsm_mirror", name: "Blind Spot Mirror Camera" },
  { key: "adaptive_headlamp", name: "Adaptive Headlamp (AFS)" },
  { key: "rear_view_camera", name: "Rear-view Camera" },
  { key: "surround_view", name: "Surround / 360° Camera" },
  { key: "lane_keep_assist", name: "Lane Keep Assist (LKA)" },
  { key: "auto_high_beam", name: "Automatic High Beam" },
];

function inferEquipped(year, make, model) {
  // Conservative heuristic based on industry rollout patterns.
  // Real production should integrate with ALLDATA / OEM build-sheet APIs.
  const y = parseInt(year) || 0;
  const m = (make || "").toLowerCase();
  const mdl = (model || "").toLowerCase();
  const isLuxury = /bmw|mercedes|audi|lexus|acura|infiniti|cadillac|tesla|porsche|jaguar|volvo/i.test(make || "");
  const result = {};
  result.front_camera = y >= 2015;
  result.front_radar = y >= 2017 || (y >= 2014 && isLuxury);
  result.parking_sensors = y >= 2016 || isLuxury;
  result.blind_spot_radar = y >= 2018 || (y >= 2014 && isLuxury);
  result.bsm_mirror = m === "honda" && y >= 2016; // Honda LaneWatch
  result.adaptive_headlamp = isLuxury && y >= 2015;
  result.rear_view_camera = y >= 2018; // mandatory in US from 2018
  result.surround_view = isLuxury && y >= 2017;
  result.lane_keep_assist = y >= 2018;
  result.auto_high_beam = y >= 2019 || (y >= 2016 && isLuxury);
  return result;
}

export default function ADASAnalysis() {
  const [vin, setVin] = useState("");
  const [decode, setDecode] = useState(null);
  const [loading, setLoading] = useState(false);

  const decodeVin = async () => {
    const v = (vin || "").trim().toUpperCase();
    if (!v || v.length < 11) {
      toast.error("VIN required (min 11 chars)");
      return;
    }
    setLoading(true);
    try {
      // Real NHTSA decode via /vin/decode/{vin}
      const res = await api.get(`/vin/decode/${v}`);
      const d = res.data || {};
      const equipped = inferEquipped(d.year, d.make, d.model);
      const systems = ADAS_SYSTEMS.map((s) => ({ ...s, equipped: !!equipped[s.key] }));
      setDecode({
        vin: v,
        year: d.year,
        make: d.make,
        model: d.model,
        trim: d.trim,
        body_class: d.body_class,
        manufacturer: d.manufacturer,
        plant_country: d.plant_country,
        adas_systems: systems,
      });
      toast.success(`Decoded · ${d.year || ""} ${d.make || ""} ${d.model || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "VIN decode failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-up max-w-5xl" data-testid="adas-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">ADAS Intelligence</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight">VIN Decoder & ADAS Analysis</h1>
        <p className="text-sm text-slate-400 mt-1.5">Real NHTSA decode + safety systems inference per OEM rollout patterns.</p>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
        <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">VIN</Label>
        <div className="flex gap-2 mt-1.5">
          <Input
            data-testid="adas-vin-input"
            className="bg-[#030712] border-slate-800 font-mono flex-1 h-11 text-base"
            value={vin}
            onChange={(e) => setVin(e.target.value.toUpperCase())}
            placeholder="17-char VIN — e.g. 4S3BWAF61M3020433"
            maxLength={17}
            onKeyDown={(e) => e.key === "Enter" && decodeVin()}
          />
          <Button data-testid="decode-vin-btn" onClick={decodeVin} disabled={loading} className="bg-blue-600 hover:bg-blue-500 h-11 px-5">
            <Hash className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> {loading ? "Decoding…" : "Decode VIN"}
          </Button>
        </div>
      </div>

      {decode && (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 animate-slide-up" data-testid="adas-result">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-400">VIN Decoded · NHTSA</div>
              <h3 className="font-display text-2xl font-medium mt-1">{decode.year} {decode.make} {decode.model}{decode.trim ? ` ${decode.trim}` : ""}</h3>
              <div className="font-mono text-sm text-blue-300 mt-1">{decode.vin}</div>
            </div>
            <Sparkles className="w-8 h-8 text-cyan-400" />
          </div>

          {/* Real vehicle details from NHTSA */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[
              { label: "Body Class", value: decode.body_class },
              { label: "Manufacturer", value: decode.manufacturer },
              { label: "Plant Country", value: decode.plant_country },
              { label: "Model Year", value: decode.year },
            ].filter(f => f.value).map((f, i) => (
              <div key={i} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">{f.label}</div>
                <div className="text-sm text-slate-200 mt-1 truncate" title={String(f.value)}>{f.value}</div>
              </div>
            ))}
          </div>

          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Likely ADAS Systems (heuristic)</div>
          <div className="grid md:grid-cols-2 gap-2">
            {decode.adas_systems.map((s) => (
              <div key={s.key} className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-900/40 border border-slate-800/60">
                <span className="text-sm">{s.name}</span>
                <Badge className={s.equipped ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-slate-800 text-slate-500 border border-slate-700/40"}>
                  {s.equipped ? "LIKELY" : "N/A"}
                </Badge>
              </div>
            ))}
          </div>

          <div className="mt-5 p-4 rounded-lg bg-amber-500/5 border border-amber-500/20 flex gap-3">
            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <div className="text-xs text-slate-400 leading-relaxed">
              <span className="text-amber-400 font-medium">Vehicle details:</span> sourced from <b>NHTSA VPIC</b> (real).{" "}
              <span className="text-amber-400 font-medium">ADAS systems:</span> heuristic inference based on year, brand and industry rollout patterns. For exact build-sheet, integrate with ALLDATA or the OEM portal.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
